import type {
  AddReactionMutationMutation,
  AddReactionMutationMutationVariables,
} from '@github-ui/server-persisted-queries/types/addReactionMutation'
import type {
  RemoveReactionMutationMutation,
  RemoveReactionMutationMutationVariables,
} from '@github-ui/server-persisted-queries/types/removeReactionMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'

type AddReactionSubject = NonNullable<NonNullable<AddReactionMutationMutation['addReaction']>['subject']>
type RemoveReactionSubject = NonNullable<NonNullable<RemoveReactionMutationMutation['removeReaction']>['subject']>

export type ReactionSubject = AddReactionSubject | RemoveReactionSubject

export type ReactionMutationVariables = AddReactionMutationMutationVariables | RemoveReactionMutationMutationVariables

export type ReactionMutationCallbacks<TSubject extends ReactionSubject> = {
  onCompleted?: (subject: TSubject) => void
  onError?: (error: Error) => void
  onStale?: () => void
  optimisticFields?: Record<string, unknown>
}

export type ReactionMutationContext = {
  mutation: symbol
}

const latestReactionMutations = new Map<string, symbol>()

type GraphQLResponse<T> = {
  readonly data?: T
  readonly errors?: ReadonlyArray<{readonly message?: string}>
}

export async function submitReactionMutation<T>(
  method: 'DELETE' | 'POST',
  {input: {subjectId, content}}: ReactionMutationVariables,
): Promise<T> {
  const response = await reactFetchJSON('/reactable_item/reactions', {
    method,
    body: {subjectId, content},
  })

  if (!response.ok) throw new Error(`Reaction mutation failed (${response.status})`)
  const json = (await response.json()) as GraphQLResponse<T>
  if (!json.data) throw new Error(json.errors?.[0]?.message ?? 'Reaction mutation returned no data')

  return json.data
}

export function startReactionMutation(subject: string, mutation = Symbol()) {
  latestReactionMutations.set(subject, mutation)
  return mutation
}

export function isLatestReactionMutation(subject: string, mutation: symbol) {
  return latestReactionMutations.get(subject) === mutation
}

export function completeReactionMutation(subject: string, mutation: symbol) {
  if (latestReactionMutations.get(subject) !== mutation) return false

  latestReactionMutations.delete(subject)
  return true
}
